// src/components/dashboard/stats/HistoryYearChart.tsx
// ✅ GRÁFICO ANUAL - CORRIGIDO COM GRID ARREDONDADO E TOOLTIPS

import React, { useEffect, useState } from 'react';

interface YearHistoryMonth {
    month: number;
    monthName: string;
    cardsStudied: number;
}

interface HistoryYearChartProps {
    history: YearHistoryMonth[];
    animate: boolean;
}

const HistoryYearChart: React.FC<HistoryYearChartProps> = ({ history, animate }) => {
    const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

    const width = 900;
    const height = 250;
    const padding = 50;
    const topPadding = 40; // ✅ Reduzido de 60 para 40

    useEffect(() => {
        if (animate && history.length > 0) {
            const path = document.querySelector('.dsb-estx-hist-line-path') as SVGPathElement;
            if (path) {
                const length = path.getTotalLength();
                path.style.strokeDasharray = `${length}`;
                path.style.strokeDashoffset = `${length}`;

                setTimeout(() => {
                    path.style.transition = 'stroke-dashoffset 1.8s ease';
                    path.style.strokeDashoffset = '0';
                }, 100);
            }
        }
    }, [animate, history.length]);

    if (history.length === 0) {
        return (
            <div className="dsb-estx-hist-chart">
                <h3>Evolução Anual por Mês</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                    Nenhum dado disponível no histórico
                </p>
            </div>
        );
    }

    const maxCards = Math.max(...history.map(m => m.cardsStudied), 1);

    // ✅ GRID ARREDONDADO: Calcular valores redondos (50, 100, 150...)
    const calculateGridValues = (max: number): number[] => {
        // Arredondar para cima para o próximo múltiplo de 50
        let roundedMax = Math.ceil(max / 50) * 50;

        // Se for muito pequeno (< 10), usar incrementos de 2
        if (max <= 10) {
            const step = 2;
            roundedMax = Math.ceil(max / step) * step;
            return [0, step, step * 2, step * 3, step * 4, step * 5].filter(v => v <= roundedMax);
        }

        // Determinar incremento baseado no valor máximo
        let step = 50;
        if (roundedMax > 500) step = 100;
        if (roundedMax > 1000) step = 200;
        if (roundedMax > 2000) step = 500;

        // ✅ CORREÇÃO: Garantir que roundedMax seja múltiplo do step
        roundedMax = Math.ceil(max / step) * step;

        const values: number[] = [];
        for (let i = 0; i <= roundedMax; i += step) {
            values.push(i);
        }

        // ✅ Retornar TODOS os valores (sem limit de 6)
        return values;
    };

    const gridValues = calculateGridValues(maxCards);
    const gridMax = gridValues[gridValues.length - 1];

    // Calcular pontos do gráfico (12 meses)
    const points = history.map((month, index) => {
        const x = padding + (index / (history.length - 1)) * (width - 2 * padding);
        // ✅ Usar gridMax ao invés de maxCards para proporcionalidade correta
        const y = topPadding + height - padding - (month.cardsStudied / gridMax) * (height - 2 * padding);
        return {
            x,
            y,
            month: month.monthName,
            cards: month.cardsStudied,
            monthNumber: month.month
        };
    });

    // Criar path SVG
    const pathData = points.reduce((path, point, index) => {
        return path + (index === 0 ? `M ${point.x} ${point.y}` : ` L ${point.x} ${point.y}`);
    }, '');

    return (
        <div className="dsb-estx-hist-chart">
            <h3>Evolução Anual por Mês</h3>

            <svg
                className="dsb-estx-hist-svg"
                viewBox={`0 0 ${width} ${height + topPadding}`}
                style={{ overflow: 'visible' }}
            >
                {/* ✅ Grid horizontal com valores arredondados */}
                {gridValues.map((value, i) => {
                    const y = topPadding + height - padding - (value / gridMax) * (height - 2 * padding);
                    return (
                        <g key={i}>
                            <line
                                className="dsb-estx-hist-grid"
                                x1={padding}
                                y1={y}
                                x2={width - padding}
                                y2={y}
                            />
                            <text
                                className="dsb-estx-hist-label"
                                x={padding - 10}
                                y={y + 4}
                                textAnchor="end"
                                style={{ fontSize: '11px' }}
                                fill="var(--text-secondary)"
                            >
                                {value}
                            </text>
                        </g>
                    );
                })}

                {/* Linha principal */}
                <path
                    className="dsb-estx-hist-line-path"
                    d={pathData}
                />

                {/* ✅ Pontos com tooltip igual ao gráfico mensal */}
                {points.map((point, i) => (
                    <circle
                        key={i}
                        className="dsb-estx-hist-point"
                        cx={point.x}
                        cy={point.y}
                        r="5"
                        style={{
                            cursor: 'pointer',
                            animationDelay: `${0.5 + i * 0.1}s`
                        }}
                        onMouseEnter={() => setTooltip({
                            x: point.x,
                            y: point.y - 20,
                            text: `${point.month}: ${point.cards} cards`
                        })}
                        onMouseLeave={() => setTooltip(null)}
                    />
                ))}

                {/* ✅ Tooltip HTML */}
                {tooltip && (
                    <foreignObject
                        x={tooltip.x - 50}
                        y={tooltip.y - 32}
                        width="130"
                        height="28"
                    >
                        <div
                            style={{
                                background: 'var(--btn-primary)',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '5px',
                                fontSize: '11px',
                                fontWeight: 600,
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 2px 6px var(--platform-a25)',
                                pointerEvents: 'none',
                                lineHeight: 1.2
                            }}
                        >
                            {tooltip.text}
                        </div>
                    </foreignObject>
                )}

                {/* Labels dos meses */}
                {points.map((point, i) => (
                    <text
                        key={i}
                        className="dsb-estx-hist-label"
                        x={point.x}
                        y={topPadding + height - padding + 25}
                        textAnchor="middle"
                        style={{ fontSize: '10px' }}
                        fill="var(--text-secondary)"
                    >
                        {point.month.substring(0, 3)}
                    </text>
                ))}
            </svg>
        </div>
    );
};

export default HistoryYearChart;